import axios, {AxiosInstance, AxiosError} from "axios";
import type {AppStore} from "../store";
import {forceRestricted} from "../features/billing/model/slice";
import {fetchBillingStatus} from "../features/billing/model/actions";

// ── Расширение типов axios ──────────────────────────────────────────────────
// Добавляем кастомные поля в AxiosRequestConfig, чтобы их можно было
// передавать в конфиге запроса и читать в интерсепторах без any/приведений.
//
//   _skipBillingHandler — пометка, что данный запрос НЕ должен триггерить
//   billing-обработку при 402. Используется в billingAPI.getStatus(),
//   чтобы избежать рекурсии fetchBillingStatus → 402 → fetchBillingStatus.
//
//   _retry — флаг, что для этого запроса уже был выполнен refresh access-токена.
//   Защищает от бесконечного цикла повторов при 401.
declare module "axios" {
    export interface AxiosRequestConfig {
        _skipBillingHandler?: boolean;
        _retry?: boolean;
    }

    export interface InternalAxiosRequestConfig {
        _skipBillingHandler?: boolean;
        _retry?: boolean;
    }
}

// ── HTTP-пути и инстанс axios ──────────────────────────────────────────────

export const nestServerPath = process.env.REACT_APP_NEST_SERVER_PATH ?? "/api";
export const fileServerPath = process.env.REACT_APP_FILE_SERVER_PATH ?? "/api/file/static";
export const websocketPath = process.env.REACT_APP_WEBSOCKET_PATH ?? "";

export const appAPI: AxiosInstance = axios.create({
    baseURL: nestServerPath,
    withCredentials: true,
});

// ── Ленивая инъекция store ─────────────────────────────────────────────────
// store импортируется не напрямую (иначе циклическая зависимость),
// а через injectStore(store) — вызов в index.tsx после setupStore().

let storeRef: AppStore | null = null;

export const injectStore = (store: AppStore) => {
    storeRef = store;
};

// ── Auth helpers ────────────────────────────────────────────────────────────

export async function getNewAccessToken(): Promise<string> {
    const response = await fetch(`${nestServerPath}/auth/refresh`, {
        method: "POST",
        credentials: "include",
    });
    if (!response.ok) {
        throw new Error("Не удалось обновить токен");
    }
    const data = await response.json();
    return data.access_token as string;
}

// ── Request interceptor: подкладывает access-токен ─────────────────────────

appAPI.interceptors.request.use((config) => {
    const token = localStorage.getItem("access_token");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// ── Response interceptor ────────────────────────────────────────────────────
//
// Логика:
//   1. 402 + error: "billing_restricted" → синхронизируем стор:
//      forceRestricted() мгновенно блокирует UI, fetchBillingStatus()
//      догружает актуальные числа фоном.
//      Запросы с _skipBillingHandler пропускаются, чтобы не было рекурсии.
//   2. 401 → пытаемся обновить access-токен через refresh-cookie,
//      затем повторяем оригинальный запрос (один раз, через _retry).
//   3. Остальное — реджектим без вмешательства.

appAPI.interceptors.response.use(
    (resp) => resp,
    async (error: AxiosError) => {
        const originalRequest = error.config;

        // ── 1. Billing 402 ──
        if (error.response?.status === 402) {
            // Запрос помечен флагом → не реагируем, чтобы избежать рекурсии
            // (это сам fetchBillingStatus или другой служебный запрос).
            if (originalRequest?._skipBillingHandler) {
                return Promise.reject(error);
            }

            const data = error.response.data as
                | { error?: string; message?: string }
                | undefined;

            if (data?.error === "billing_restricted" && storeRef) {
                storeRef.dispatch(forceRestricted());
                // Догружаем актуальные числа фоном — без await, чтобы не задерживать reject
                void storeRef.dispatch(fetchBillingStatus());
            }
            return Promise.reject(error);
        }

        // ── 2. 401 → refresh ──
        const is401 = error.response?.status === 401;
        const isRefreshCall = originalRequest?.url?.includes("/auth/refresh");
        const alreadyRetried = originalRequest?._retry;
        const hasAccessToken = !!localStorage.getItem("access_token");

        if (
            is401 &&
            !isRefreshCall &&
            !alreadyRetried &&
            hasAccessToken &&
            originalRequest
        ) {
            originalRequest._retry = true;
            try {
                const newToken = await getNewAccessToken();
                localStorage.setItem("access_token", newToken);
                appAPI.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
                originalRequest.headers.set("Authorization", `Bearer ${newToken}`);
                return appAPI(originalRequest);
            } catch (refreshError) {
                localStorage.removeItem("access_token");
                return Promise.reject(refreshError);
            }
        }

        // ── 3. Прочее ──
        return Promise.reject(error);
    },
);